import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

import {
  ArrowLeft,
  CheckCircle2,
  FileCheck2,
  FileSpreadsheet,
  FileText,
  RotateCcw,
  Search,
  Download,
  FileDown,
  UploadCloud,
  XCircle,
  Lock,
  Plus,
  Scale,
  Trash2,
  Upload,
  X,
  Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './ConferenciaValoresExames.css';

const normalizar = (valor) =>
  String(valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const numero = (valor) => {
  const texto = String(valor ?? '').trim();

  if (!texto) return null;

  const limpo = texto
    .replace(/[R$\s]/g, '')
    .replace(/\.(?=\d{3}(?:,|$))/g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');

  const resultado = Number(limpo);
  return Number.isFinite(resultado) ? resultado : null;
};

const formatarValor = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '—';

  const convertido = numero(valor);
  if (convertido === null) return String(valor);

  return convertido.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const nomeArquivo = (arquivo) => arquivo?.name || 'Nenhum arquivo selecionado';


const extrairCompetencia = (arquivo) => {
  const nome = String(arquivo?.name || '');

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const nomeNormalizado = normalizar(nome);

  const indiceMes = meses.findIndex((mes) =>
    nomeNormalizado.includes(normalizar(mes))
  );

  let ano = null;

  const anoCompleto = nome.match(/(?:19|20)\d{2}/);
  if (anoCompleto) {
    ano = anoCompleto[0];
  }

  const mesAno = nome.match(/(?:MES|M[ÊE]S)?\s*(0?[1-9]|1[0-2])[\s._-]*(\d{2,4})/i);

  if (!ano && mesAno) {
    ano = mesAno[2].length === 2
      ? `20${mesAno[2]}`
      : mesAno[2];
  }

  if (indiceMes >= 0) {
    return `${meses[indiceMes]}/${ano || ''}`.replace(/\/$/, '');
  }

  if (mesAno) {
    const mes = Number(mesAno[1]);
    const anoNumerico = mesAno[2].length === 2
      ? `20${mesAno[2]}`
      : mesAno[2];

    return `${meses[mes - 1]}/${anoNumerico}`;
  }

  return 'Competência não identificada';
};

const nomeSeguroArquivo = (valor) =>
  String(valor || 'conferencia')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

const exportarExcelResultado = (resultado, referencia, fatura) => {
  if (!resultado) return;

  const linhas = resultado.linhas.map((item) => ({
    Procedimento: item.descricao,
    Código: item.codigo || '',
    Quantidade: item.quantidade,
    'Pago / un.': item.pagoUnitario ?? '',
    'Referência / un.': item.referenciaUnitario ?? '',
    'Dif. / un.': item.diferencaUnitario ?? '',
    'Diferença total': item.diferencaTotal ?? '',
    'Diferença %': item.percentual == null
      ? ''
      : `${item.percentual.toLocaleString('pt-BR', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        })}%`
  }));

  linhas.push({});
  linhas.push({
    Procedimento: 'RESUMO',
    'Pago / un.': resultado.totalPago,
    'Referência / un.': resultado.totalReferencia,
    'Diferença total': resultado.diferencaTotal
  });

  const planilha = XLSX.utils.json_to_sheet(linhas);
  planilha['!cols'] = [
    { wch: 42 },
    { wch: 16 },
    { wch: 12 },
    { wch: 16 },
    { wch: 18 },
    { wch: 14 },
    { wch: 18 },
    { wch: 14 }
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, planilha, 'Conferência');

  const competencia = extrairCompetencia(fatura);
  XLSX.writeFile(
    workbook,
    `conferencia-valores-${nomeSeguroArquivo(competencia)}.xlsx`
  );
};

const escaparHtml = (valor) =>
  String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const exportarPdfResultado = (resultado, referencia, fatura) => {
  if (!resultado) return;

  const competencia = extrairCompetencia(fatura);
  const janela = window.open('', '_blank', 'width=1200,height=800');

  if (!janela) {
    window.alert('Permita pop-ups para exportar o PDF.');
    return;
  }

  const linhas = resultado.linhas.map((item) => `
    <tr>
      <td>
        <strong>${escaparHtml(item.descricao)}</strong>
        ${item.codigo ? `<small>Cód. ${escaparHtml(item.codigo)}</small>` : ''}
      </td>
      <td>${escaparHtml(item.quantidade)}</td>
      <td>${escaparHtml(formatarValor(item.pagoUnitario))}</td>
      <td>${escaparHtml(formatarValor(item.referenciaUnitario))}</td>
      <td>${escaparHtml(formatarValor(item.diferencaUnitario))}</td>
      <td>${escaparHtml(formatarValor(item.diferencaTotal))}</td>
      <td>${item.percentual == null ? '—' : `${item.percentual.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}%`}</td>
    </tr>
  `).join('');

  janela.document.write(`
    <!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="utf-8" />
        <title>Conferência de valores - ${escaparHtml(competencia)}</title>
        <style>
          @page { size: A4 landscape; margin: 12mm; }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            color: #26384b;
            font-family: Arial, sans-serif;
            font-size: 10px;
          }
          h1 { margin: 0 0 5px; font-size: 20px; }
          h2 { margin: 18px 0 8px; font-size: 13px; }
          .meta { color: #657587; margin-bottom: 14px; }
          .summary {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
            margin: 12px 0;
          }
          .summary div {
            padding: 9px;
            border: 1px solid #d9e2e8;
            border-radius: 5px;
          }
          .summary strong {
            display: block;
            color: #16728a;
            font-size: 15px;
          }
          .summary span { color: #718294; }
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }
          th, td {
            padding: 7px 6px;
            border: 1px solid #d9e2e8;
            text-align: center;
            vertical-align: middle;
          }
          th {
            background: #f2f6f8;
            color: #526879;
            font-size: 8px;
            text-transform: uppercase;
          }
          th:first-child, td:first-child {
            width: 34%;
            text-align: left;
          }
          td:first-child strong, td:first-child small {
            display: block;
          }
          td:first-child small {
            margin-top: 2px;
            color: #8493a0;
            font-size: 8px;
          }
        </style>
      </head>
      <body>
        <h1>Conferência de valores de exames</h1>
        <div class="meta">
          Competência: <strong>${escaparHtml(competencia)}</strong>  

          Referência: ${escaparHtml(nomeArquivo(referencia))}  

          Fatura: ${escaparHtml(nomeArquivo(fatura))}
        </div>

        <div class="summary">
          <div><strong>${resultado.totalItens}</strong><span>Itens comparados</span></div>
          <div><strong>${escaparHtml(formatarValor(resultado.totalPago))}</strong><span>Total pago</span></div>
          <div><strong>${escaparHtml(formatarValor(resultado.totalReferencia))}</strong><span>Total de referência</span></div>
          <div><strong>${escaparHtml(formatarValor(resultado.diferencaTotal))}</strong><span>Diferença total</span></div>
        </div>

        <h2>Resultado da comparação</h2>

        <table>
          <thead>
            <tr>
              <th>Procedimento</th>
              <th>Qtd.</th>
              <th>Pago / un.</th>
              <th>Referência / un.</th>
              <th>Dif. / un.</th>
              <th>Diferença total</th>
              <th>Diferença %</th>
            </tr>
          </thead>
          <tbody>${linhas}</tbody>
        </table>
      </body>
    </html>
  `);

  janela.document.close();
  janela.focus();
  janela.document.title = `Conferência ${competencia}`;
  janela.setTimeout(() => janela.print(), 350);
};

const encontrarCampo = (cabecalhos, palavras) => {
  const encontrado = cabecalhos.find((campo) => {
    const nome = normalizar(campo);
    return palavras.some((palavra) => nome.includes(palavra));
  });

  return encontrado || cabecalhos[0];
};

const normalizarProcedimento = (valor) =>
  String(valor ?? '')
    // Mantém o texto dentro de parênteses/colchetes, como ESCARRO.
    .replace(/[\[\]\(\)]/g, ' ')
    // Remove códigos TUSS/CBHPM ou números longos embutidos no nome.
    .replace(/\b\d{6,12}\b/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const chavesProcedimento = (valor) => {
  const base = normalizarProcedimento(valor);
  const chaves = [];

  if (base) chaves.push(base);

  // Permite comparar, por exemplo:
  // BAAR - PESQUISA (ESCARRO)
  // BAAR - PESQUISA (ESCARRO) 2
  // BAAR - PESQUISA (ESCARRO) 3
  const semNumeroFinal = base.replace(/\s+\d+$/, '').trim();

  if (semNumeroFinal && !chaves.includes(semNumeroFinal)) {
    chaves.push(semNumeroFinal);
  }

  return chaves;
};


const normalizarCabecalho = (valor) =>
  normalizarProcedimento(valor).replace(/\s+/g, ' ');

const encontrarColuna = (cabecalhos, termos) =>
  cabecalhos.findIndex((cabecalho) =>
    termos.some((termo) => normalizarCabecalho(cabecalho).includes(termo))
  );

const encontrarColunaPreferida = (cabecalhos, termos) => {
  const indice = cabecalhos.findIndex((cabecalho) => {
    const nome = normalizarCabecalho(cabecalho);
    return termos.some((termo) => nome === termo || nome.includes(termo));
  });

  return indice;
};

const criarLinhasPlanilha = (matrix, tipo = 'referencia') => {
  if (!Array.isArray(matrix) || matrix.length < 2) return [];

  let linhaCabecalho = 0;
  let cabecalhos = [];

  for (let indice = 0; indice < Math.min(matrix.length, 30); indice += 1) {
    const candidatos = (matrix[indice] || []).map((valor) => String(valor ?? ''));
    const nomes = candidatos.map(normalizarCabecalho);

    const temNome = nomes.some((nome) =>
      ['procedimento', 'exame', 'descricao', 'nome', 'item', 'servico']
        .some((termo) => nome.includes(termo))
    );

    const temValor = nomes.some((nome) =>
      ['valor', 'preco', 'preco', 'pago', 'faturado', 'referencia', 'total']
        .some((termo) => nome.includes(termo))
    );

    if (temNome && temValor) {
      linhaCabecalho = indice;
      cabecalhos = candidatos;
      break;
    }
  }

  if (!cabecalhos.length) {
    cabecalhos = (matrix[0] || []).map((valor, indice) =>
      String(valor || `coluna_${indice + 1}`)
    );
  }

  const nomes = cabecalhos.map(normalizarCabecalho);

  const colunaNome = encontrarColunaPreferida(nomes, [
    'procedimento',
    'nome do procedimento',
    'exame',
    'descricao do exame',
    'descricao',
    'servico',
    'item',
    'nome'
  ]);

  const colunaCodigo = encontrarColunaPreferida(nomes, [
    'codigo',
    'cod',
    'tuss',
    'cbhpm'
  ]);

  const colunaQuantidade = encontrarColunaPreferida(nomes, [
    'quantidade',
    'qtd',
    'qtde',
    'quant',
    'unidades',
    'unid'
  ]);

  const colunaUnitario = encontrarColunaPreferida(nomes, [
    'valor unitario',
    'preco unitario',
    'valor por unidade',
    'unitario'
  ]);

  const colunaTotal = encontrarColunaPreferida(nomes, [
    'valor total',
    'preco total',
    'total',
    'subtotal'
  ]);

  const termosValor = tipo === 'fatura'
    ? ['valor pago', 'valor cobrado', 'valor faturado', 'pago', 'cobrado', 'faturado']
    : ['valor de referencia', 'valor referencia', 'valor referencial', 'referencia', 'contratado'];

  let colunaValor = encontrarColunaPreferida(nomes, termosValor);

  if (colunaValor < 0) {
    colunaValor = colunaUnitario >= 0
      ? colunaUnitario
      : colunaTotal >= 0
        ? colunaTotal
        : encontrarColuna(nomes, ['valor', 'preco', 'total']);
  }

  const parseQuantidade = (valor) => {
    const resultado = numero(valor);
    return resultado && resultado > 0 ? resultado : 1;
  };

  return matrix
    .slice(linhaCabecalho + 1)
    .map((linha, indice) => {
      const descricao = String(
        linha[colunaNome >= 0 ? colunaNome : 0] ?? ''
      ).trim();

      const codigo = String(
        linha[colunaCodigo >= 0 ? colunaCodigo : -1] ?? ''
      ).trim();

      if (
        !descricao ||
        /^(sub\s*total|total geral|total da fatura|procedimento|exame|descricao)$/i
          .test(descricao)
      ) {
        return null;
      }

      const quantidade = colunaQuantidade >= 0
        ? parseQuantidade(linha[colunaQuantidade])
        : 1;

      const valorBruto = colunaValor >= 0
        ? linha[colunaValor]
        : '';

      const totalBruto = colunaTotal >= 0
        ? linha[colunaTotal]
        : '';

      const valorInformado = numero(valorBruto);
      const totalInformado = numero(totalBruto);

      if (valorInformado === null && totalInformado === null) {
        return null;
      }

      const valorUnitario = colunaTotal >= 0 && colunaValor < 0
        ? (totalInformado || 0) / quantidade
        : valorInformado;

      const total = colunaTotal >= 0 && colunaValor < 0
        ? totalInformado
        : (valorUnitario || 0) * quantidade;

      return {
        id: `${tipo}-${indice}-${normalizarProcedimento(descricao)}`,
        chave: normalizarProcedimento(descricao),
        codigo,
        descricao,
        quantidade,
        valor: valorUnitario,
        valorNumero: valorUnitario,
        valorUnitario,
        total,
        origem: tipo
      };
    })
    .filter(Boolean);
};


const criarLinhasPdf = async (arquivo, tipo = 'fatura') => {
  const dados = new Uint8Array(await arquivo.arrayBuffer());

  const documento = await pdfjsLib.getDocument({
    data: dados,
    disableWorker: false
  }).promise;

  const linhas = [];

  for (
    let numeroPagina = 1;
    numeroPagina <= documento.numPages;
    numeroPagina += 1
  ) {
    const pagina = await documento.getPage(numeroPagina);
    const conteudo = await pagina.getTextContent();
    const rotacao = Number(pagina.rotate || 0);

    const itens = conteudo.items
      .map((item) => ({
        texto: String(item.str || '').trim(),
        x: Number(item.transform?.[4] || 0),
        y: Number(item.transform?.[5] || 0),
        largura: Number(item.width || 0)
      }))
      .filter((item) =>
        item.texto &&
        Number.isFinite(item.x) &&
        Number.isFinite(item.y)
      );

    /*
     * O PDF do laboratório está rotacionado.
     * Nesse caso, cada exame fica em uma coluna vertical,
     * portanto o agrupamento correto é pelo eixo X.
     */
    const rotacionado = rotacao === 90 || rotacao === 270;

    const grupos = [];

    if (rotacionado) {
      itens
        .sort((a, b) => a.x - b.x || a.y - b.y)
        .forEach((item) => {
          let grupo = grupos[grupos.length - 1];

          if (!grupo || Math.abs(grupo.posicao - item.x) > 3) {
            grupo = {
              posicao: item.x,
              itens: []
            };

            grupos.push(grupo);
          }

          grupo.itens.push(item);
          grupo.posicao =
            grupo.itens.reduce((total, parte) => total + parte.x, 0) /
            grupo.itens.length;
        });
    } else {
      itens
        .sort((a, b) => b.y - a.y || a.x - b.x)
        .forEach((item) => {
          let grupo = grupos.find(
            (atual) => Math.abs(atual.posicao - item.y) <= 3
          );

          if (!grupo) {
            grupo = {
              posicao: item.y,
              itens: []
            };

            grupos.push(grupo);
          }

          grupo.itens.push(item);
        });
    }

    grupos.forEach((grupo, indiceGrupo) => {
      const ordenados = [...grupo.itens].sort((a, b) => {
        if (rotacionado) {
          return rotacao === 90 ? a.y - b.y : b.y - a.y;
        }

        return a.x - b.x;
      });

      const textos = ordenados.map((item) => item.texto);
      const textoCompleto = textos.join(' ').replace(/\s+/g, ' ').trim();

      if (!textoCompleto) return;

      const codigoItem = ordenados.find((item) =>
        /^\d{6,10}$/.test(item.texto)
      );

      let nomeItens;

      if (rotacionado) {
        nomeItens = ordenados
          .filter((item) => {
            if (codigoItem && item.texto === codigoItem.texto) {
              return false;
            }

            return item.y >= 405 && item.y < 650;
          })
          .map((item) => item.texto);
      } else {
        nomeItens = textos;
      }

      let descricao = nomeItens
        .join(' ')
        .replace(/\b\d{4,}\b/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/^[\s\-–—|:;,.]+|[\s\-–—|:;,.]+$/g, '')
        .trim();

      const descricaoNormalizada = normalizarCabecalho(descricao);

      // “Procedimento Guia” é o cabeçalho do PDF, não um exame.
      if (
        /^(procedimento guia|procedimento|procedimentos|guia|codigo|cod|data|paciente|valor|total)$/
          .test(descricaoNormalizada) ||
        descricaoNormalizada.startsWith('procedimento guia ')
      ) {
        return;
      }

      const valores = rotacionado
        ? ordenados
            .filter((item) => item.y >= 625)
            .map((item) => numero(item.texto))
            .filter((valor) => valor !== null && valor >= 0)
        : (
            textoCompleto.match(
              /(?:R\$\s*)?\d{1,3}(?:\.\d{3})*(?:,\d{2})|\d+(?:[.,]\d{2})/g
            ) || []
          )
            .map((valor) => numero(valor))
            .filter((valor) => valor !== null && valor >= 0);

      if (
        !descricao ||
        descricao.length < 3 ||
        !valores.length ||
        /^(procedimento|codigo|cod|data|paciente|valor|total)$/i.test(descricao) ||
        /sub\s*total|total geral|total da fatura/i.test(descricao)
      ) {
        return;
      }

      const valorUnitario = valores[valores.length - 1];

      if (valorUnitario === null || !Number.isFinite(valorUnitario)) {
        return;
      }

      const codigo = codigoItem?.texto || '';

      linhas.push({
        id: `pdf-${numeroPagina}-${indiceGrupo}`,
        chave: normalizarProcedimento(descricao),
        codigo,
        descricao,
        quantidade: 1,
        valor: valorUnitario,
        valorNumero: valorUnitario,
        valorUnitario,
        total: valorUnitario,
        origem: tipo,
        pagina: numeroPagina
      });
    });
  }

  return linhas.filter((linha) => linha.chave);
};


const lerArquivo = async (arquivo, tipo = 'referencia') => {
  if (!arquivo) return [];

  const extensao = arquivo.name.split('.').pop().toLowerCase();

  if (extensao === 'pdf') {
    return criarLinhasPdf(arquivo, tipo);
  }

  const dados = await arquivo.arrayBuffer();
  const workbook = XLSX.read(dados, {
    type: 'array',
    cellDates: true
  });

  const primeiraAba = workbook.SheetNames[0];
  const planilha = workbook.Sheets[primeiraAba];
  const matrix = XLSX.utils.sheet_to_json(planilha, {
    header: 1,
    defval: ''
  });

  return criarLinhasPlanilha(matrix, tipo);
};

const lerMatrizPlanilha = async (arquivo) => {
  const dados = await arquivo.arrayBuffer();
  const workbook = XLSX.read(dados, { type: 'array', cellDates: true });
  const aba = workbook.SheetNames[0];
  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[aba], { header: 1, defval: '' });
  return { matrix, aba };
};

const detectarLinhaCabecalho = (matrix) => {
  for (let i = 0; i < Math.min(matrix.length, 30); i += 1) {
    const nomes = (matrix[i] || []).map((v) => normalizarCabecalho(String(v ?? '')));
    const temNome = nomes.some((n) => ['procedimento', 'exame', 'descricao', 'nome', 'item', 'servico'].some((t) => n.includes(t)));
    const temValor = nomes.some((n) => ['valor', 'preco', 'pago', 'faturado', 'referencia', 'total'].some((t) => n.includes(t)));
    if (temNome && temValor) return i;
  }
  return 0;
};

const cabecalhosDaMatriz = (matrix, linha) =>
  (matrix[linha] || []).map((v, i) => String(v ?? '').trim() || `Coluna ${i + 1}`);

const assinaturaMatriz = (matrix) => {
  const linha = detectarLinhaCabecalho(matrix);
  return (matrix[linha] || []).map((v) => normalizarCabecalho(String(v ?? ''))).join('|');
};

const sugerirMapa = (matrix, tipo) => {
  const linhaCabecalho = detectarLinhaCabecalho(matrix);
  const nomes = (matrix[linhaCabecalho] || []).map((v) => normalizarCabecalho(String(v ?? '')));
  const achar = (termos) => nomes.findIndex((n) => termos.some((t) => n.includes(t)));
  const termosValor = tipo === 'fatura'
    ? ['pago', 'cobrado', 'faturado', 'valor', 'preco', 'total']
    : ['referencia', 'contratado', 'valor', 'preco', 'total'];
  let valor = -1;
  for (const termo of termosValor) {
    valor = nomes.findIndex((n) => n.includes(termo));
    if (valor >= 0) break;
  }
  const quantidade = achar(['quantidade', 'qtd', 'qtde', 'quant']);
  const nome = achar(['procedimento', 'exame', 'descricao', 'servico', 'item', 'nome']);
  const codigo = achar(['codigo', 'cod', 'tuss', 'cbhpm']);
  const ehTotal = valor >= 0 && /total/.test(nomes[valor]) && !/unit/.test(nomes[valor]);
  return {
    linhaCabecalho,
    nome: nome >= 0 ? nome : 0,
    valor,
    codigo,
    quantidade,
    valorTipo: ehTotal && quantidade >= 0 ? 'total' : 'unitario',
  };
};

// Mesmo formato de linha da leitura automática (criarLinhasPlanilha).
const criarLinhasMapeadas = (matrix, mapa, tipo) => {
  if (!Array.isArray(matrix) || !mapa || mapa.nome < 0 || mapa.valor < 0) return [];
  return matrix
    .slice((mapa.linhaCabecalho ?? 0) + 1)
    .map((linha, indice) => {
      const descricao = String(linha[mapa.nome] ?? '').trim();
      if (!descricao || /^(sub\s*total|total geral|total da fatura|procedimento|exame|descricao)$/i.test(descricao)) return null;
      const codigo = mapa.codigo >= 0 ? String(linha[mapa.codigo] ?? '').trim() : '';
      const qtdLida = mapa.quantidade >= 0 ? numero(linha[mapa.quantidade]) : null;
      const quantidade = qtdLida && qtdLida > 0 ? qtdLida : 1;
      const valorLido = numero(linha[mapa.valor]);
      if (valorLido === null || valorLido === undefined) return null;
      const valorUnitario = mapa.valorTipo === 'total' ? valorLido / quantidade : valorLido;
      const total = mapa.valorTipo === 'total' ? valorLido : valorUnitario * quantidade;
      return {
        id: `${tipo}-${indice}-${normalizarProcedimento(descricao)}`,
        chave: normalizarProcedimento(descricao),
        codigo,
        descricao,
        quantidade,
        valor: valorUnitario,
        valorNumero: valorUnitario,
        valorUnitario,
        total,
        origem: tipo,
      };
    })
    .filter(Boolean);
};

const comparar = (referencia, fatura) => {
  const referenciasPorNome = new Map();
  const referenciasPorCodigo = new Map();

  referencia.forEach((linha) => {
    const nomes = chavesProcedimento(linha.descricao);
    const codigo = String(linha.codigo || '').replace(/\D/g, '');

    nomes.forEach((nome) => {
      if (!nome) return;

      const atuais = referenciasPorNome.get(nome) || [];
      referenciasPorNome.set(nome, [...atuais, linha]);
    });

    if (codigo) {
      referenciasPorCodigo.set(codigo, linha);
    }
  });

  const linhas = fatura.map((linhaFatura) => {
    const nomesFatura = chavesProcedimento(linhaFatura.descricao);
    const nome = nomesFatura[0] || normalizarProcedimento(linhaFatura.descricao);
    const codigo = String(linhaFatura.codigo || '').replace(/\D/g, '');

    let candidatos = [];

    for (const chave of nomesFatura) {
      const encontrados = referenciasPorNome.get(chave) || [];

      if (encontrados.length) {
        candidatos = encontrados;
        break;
      }
    }

    if (candidatos.length > 1 && codigo) {
      const porCodigo = candidatos.filter(
        (item) => String(item.codigo || '').replace(/\D/g, '') === codigo
      );

      if (porCodigo.length) candidatos = porCodigo;
    }

    if (!candidatos.length && codigo && referenciasPorCodigo.has(codigo)) {
      candidatos = [referenciasPorCodigo.get(codigo)];
    }

    const referenciaEncontrada = candidatos.length === 1
      ? candidatos[0]
      : candidatos.length > 1
        ? candidatos.find((item) => item.valorUnitario === candidatos[0].valorUnitario) || null
        : null;

    const quantidade = Number(linhaFatura.quantidade || 1);
    const valorPagoUnitario = Number(linhaFatura.valorUnitario || linhaFatura.valorNumero || 0);
    const pagoTotal = quantidade * valorPagoUnitario;

    if (!referenciaEncontrada) {
      return {
        tipo: 'ausente',
        estado: 'não encontrado',
        chave: nome || linhaFatura.id,
        descricao: linhaFatura.descricao,
        codigo: linhaFatura.codigo,
        quantidade,
        pagoUnitario: valorPagoUnitario,
        pagoTotal,
        referenciaUnitario: null,
        referenciaTotal: null,
        diferencaUnitario: null,
        diferencaTotal: null,
        percentual: null
      };
    }

    const valorReferenciaUnitario = Number(
      referenciaEncontrada.valorUnitario ||
      referenciaEncontrada.valorNumero ||
      0
    );

    const referenciaTotal = quantidade * valorReferenciaUnitario;
    const diferencaUnitario = valorPagoUnitario - valorReferenciaUnitario;
    const diferencaTotal = pagoTotal - referenciaTotal;
    const percentual = valorReferenciaUnitario === 0
      ? null
      : (diferencaUnitario / valorReferenciaUnitario) * 100;

    return {
      tipo: Math.abs(diferencaTotal) > 0.009 ? 'valor' : 'igual',
      estado: 'comparado',
      chave: nome || linhaFatura.id,
      descricao: linhaFatura.descricao || referenciaEncontrada.descricao,
      codigo: linhaFatura.codigo || referenciaEncontrada.codigo,
      quantidade,
      pagoUnitario: valorPagoUnitario,
      pagoTotal,
      referenciaUnitario: valorReferenciaUnitario,
      referenciaTotal,
      diferencaUnitario,
      diferencaTotal,
      percentual
    };
  });

  const comparados = linhas.filter((linha) => linha.estado === 'comparado');

  return {
    linhas,
    comparados,
    diferencas: linhas.filter((linha) => linha.tipo !== 'igual'),
    totalItens: linhas.length,
    totalPago: comparados.reduce((total, linha) => total + linha.pagoTotal, 0),
    totalReferencia: comparados.reduce((total, linha) => total + linha.referenciaTotal, 0),
    diferencaTotal: comparados.reduce((total, linha) => total + linha.diferencaTotal, 0)
  };
};


export default function ConferenciaValoresExames() {
  const navigate = useNavigate();
  const [referencia, setReferencia] = useState(null);
  const [fatura, setFatura] = useState(null);
  const [linhasReferencia, setLinhasReferencia] = useState([]);
  const [linhasFatura, setLinhasFatura] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [buscaProcedimento, setBuscaProcedimento] = useState('');
  const CHAVE_REFERENCIAS = 'conferencia-referencias-v1';
  const [referenciasSalvas, setReferenciasSalvas] = useState(() => {
    try {
      const lista = JSON.parse(localStorage.getItem('conferencia-referencias-v1') || '[]');
      return Array.isArray(lista) ? lista : [];
    } catch (_) { return []; }
  });
  const [referenciaAtivaId, setReferenciaAtivaId] = useState('');
  const [avisoReferencia, setAvisoReferencia] = useState('');
  const [matrizes, setMatrizes] = useState({ referencia: null, fatura: null });
  const [ajuste, setAjuste] = useState(null);
  const [layoutAplicado, setLayoutAplicado] = useState({ referencia: '', fatura: '' });
  const [layouts, setLayouts] = useState(() => {
    try {
      const lista = JSON.parse(localStorage.getItem('conferencia-layouts-v1') || '[]');
      return Array.isArray(lista) ? lista : [];
    } catch (_) { return []; }
  });

  const encontrarLayout = (matrix) => {
    const assinatura = assinaturaMatriz(matrix);
    return layouts.find((item) => item.assinatura === assinatura) || null;
  };

  const abrirAjuste = (tipo, info = matrizes[tipo]) => {
    if (!info?.matrix) return;
    const perfil = encontrarLayout(info.matrix);
    setAjuste({ tipo, mapa: perfil ? { ...perfil.mapa } : sugerirMapa(info.matrix, tipo), nomeLayout: '' });
  };

  const previaAjuste = useMemo(() => {
    if (!ajuste) return [];
    const info = matrizes[ajuste.tipo];
    return info?.matrix ? criarLinhasMapeadas(info.matrix, ajuste.mapa, ajuste.tipo) : [];
  }, [ajuste, matrizes]);

  const aplicarAjuste = () => {
    if (!ajuste) return;
    const { tipo, mapa, nomeLayout } = ajuste;
    if (mapa.nome < 0 || mapa.valor < 0) { setErro('Indique a coluna do nome do procedimento e a do valor.'); return; }
    const linhas = previaAjuste;
    if (!linhas.length) { setErro('Com essas colunas nenhuma linha válida foi encontrada. Revise a escolha.'); return; }
    setErro('');
    setResultado(null);
    if (tipo === 'referencia') {
      setLinhasReferencia(linhas);
      if (referenciaAtivaId) {
        const lista = referenciasSalvas.map((item) => (item.id === referenciaAtivaId
          ? { ...item, linhas, itens: linhas.length, mapa, matriz: matrizes.referencia || item.matriz }
          : item));
        setReferenciasSalvas(lista);
        try { localStorage.setItem(CHAVE_REFERENCIAS, JSON.stringify(lista)); } catch (_) { /* sem espaço */ }
      }
    } else {
      setLinhasFatura(linhas);
    }
    if (nomeLayout.trim()) {
      const assinatura = assinaturaMatriz(matrizes[tipo].matrix);
      const lista = [{ id: String(Date.now()), nome: nomeLayout.trim(), assinatura, mapa }, ...layouts.filter((item) => item.assinatura !== assinatura)];
      setLayouts(lista);
      try { localStorage.setItem('conferencia-layouts-v1', JSON.stringify(lista)); } catch (_) { /* sem espaço */ }
      setLayoutAplicado((atual) => ({ ...atual, [tipo]: nomeLayout.trim() }));
    }
    setAjuste(null);
  };

  const formatarTamanho = (bytes) => {
    const valor = Number(bytes) || 0;
    if (valor >= 1024 * 1024) return `${(valor / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
    return `${Math.max(1, Math.round(valor / 1024))} KB`;
  };

  const usarReferenciaSalva = (id, lista = referenciasSalvas) => {
    const salva = lista.find((item) => item.id === id);
    if (!salva) return;
    setReferencia({ name: salva.nome, size: salva.tamanho, salvaNoNavegador: true });
    setLinhasReferencia(Array.isArray(salva.linhas) ? salva.linhas : []);
    setMatrizes((atuais) => ({ ...atuais, referencia: salva.matriz || null }));
    setLayoutAplicado((atuais) => ({ ...atuais, referencia: '' }));
    setReferenciaAtivaId(id);
    setResultado(null);
    setErro('');
  };

  const excluirReferencia = (id) => {
    if (!window.confirm('Remover esta referência deste navegador?')) return;
    const lista = referenciasSalvas.filter((item) => item.id !== id);
    setReferenciasSalvas(lista);
    try { localStorage.setItem(CHAVE_REFERENCIAS, JSON.stringify(lista)); } catch (_) { /* sem espaço */ }
    if (referenciaAtivaId === id) {
      setReferenciaAtivaId('');
      setReferencia(null);
      setLinhasReferencia([]);
      setResultado(null);
    }
  };

  // Ao carregar uma referência nova, guarda neste navegador.
  useEffect(() => {
    if (!(referencia instanceof File) || !linhasReferencia.length) return;
    const id = `${referencia.name}::${referencia.size}`;
    const entrada = {
      id,
      nome: referencia.name,
      tamanho: referencia.size,
      itens: linhasReferencia.length,
      salvaEm: new Date().toISOString(),
      linhas: linhasReferencia,
      matriz: matrizes.referencia || null,
    };
    setReferenciasSalvas((atuais) => {
      const lista = [entrada, ...atuais.filter((item) => item.id !== id)];
      try {
        localStorage.setItem('conferencia-referencias-v1', JSON.stringify(lista));
        setAvisoReferencia('');
      } catch (_) {
        setAvisoReferencia('Não foi possível guardar a referência neste navegador (espaço cheio). Ela vale só até fechar a página.');
      }
      return lista;
    });
    setReferenciaAtivaId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referencia, linhasReferencia]);

  // Ao abrir a tela, já usa a última referência salva.
  useEffect(() => {
    if (!referencia && referenciasSalvas.length) usarReferenciaSalva(referenciasSalvas[0].id, referenciasSalvas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const podeCalcular = referencia && fatura && !carregando;

  const selecionarArquivo = async (arquivo, tipo) => {
    if (!arquivo) return;

    setErro('');
    setResultado(null);

    try {
      setCarregando(true);
      setAjuste(null);
      let linhas = await lerArquivo(arquivo, tipo);
      const ehPdf = arquivo.name.split('.').pop().toLowerCase() === 'pdf';
      const matrizInfo = ehPdf ? null : await lerMatrizPlanilha(arquivo);
      let nomeLayoutAplicado = '';
      if (matrizInfo) {
        const perfil = encontrarLayout(matrizInfo.matrix);
        if (perfil) {
          const mapeadas = criarLinhasMapeadas(matrizInfo.matrix, perfil.mapa, tipo);
          if (mapeadas.length) { linhas = mapeadas; nomeLayoutAplicado = perfil.nome; }
        }
      }
      setMatrizes((atuais) => ({ ...atuais, [tipo]: matrizInfo }));
      setLayoutAplicado((atuais) => ({ ...atuais, [tipo]: nomeLayoutAplicado }));
      if (!linhas.length && matrizInfo) {
        if (tipo === 'referencia') { setReferencia(arquivo); setLinhasReferencia([]); }
        else { setFatura(arquivo); setLinhasFatura([]); }
        abrirAjuste(tipo, matrizInfo);
        setErro('Não reconheci as colunas deste arquivo automaticamente. Indique-as no ajuste abaixo.');
        return;
      }

      if (!linhas.length) {
        throw new Error(
          'Não foi possível encontrar linhas de dados nesse arquivo.'
        );
      }

      if (tipo === 'referencia') {
        setReferencia(arquivo);
        setLinhasReferencia(linhas);
      } else {
        setFatura(arquivo);
        setLinhasFatura(linhas);
      }
    } catch (error) {
      setErro(error.message || 'Não foi possível ler o arquivo.');
    } finally {
      setCarregando(false);
    }
  };

  const calcular = () => {
    const comparacao = comparar(linhasReferencia, linhasFatura);

    setResultado({
      ...comparacao,
      calculadoEm: new Date()
    });
  };

  const reiniciar = () => {
    setReferencia(null);
    setFatura(null);
    setLinhasReferencia([]);
    setLinhasFatura([]);
    setResultado(null);
    setBuscaProcedimento('');
    setErro('');
  };

  const resumo = useMemo(() => {
    if (!resultado) return null;

    return {
      total: resultado.totalItens,
      valores: resultado.diferencas.filter((item) => item.tipo === 'valor').length,
      ausentes: resultado.diferencas.filter((item) => item.tipo === 'ausente').length,
      iguais: resultado.linhas.filter((item) => item.tipo === 'igual').length,
      totalPago: resultado.totalPago,
      totalReferencia: resultado.totalReferencia,
      diferencaTotal: resultado.diferencaTotal
    };
  }, [resultado]);

  const linhasResultado = useMemo(() => {
    if (!resultado) return [];

    const termo = normalizar(buscaProcedimento);

    if (!termo) return resultado.linhas;

    return resultado.linhas.filter((item) =>
      normalizar(`${item.descricao} ${item.codigo || ''}`).includes(termo)
    );
  }, [resultado, buscaProcedimento]);

  return (
    <main className={`conferencia-page ${resultado ? 'cvx-com-resultado' : 'cvx-inicio'}`}>
      <header className="cvx-header">
        <span className="cvx-kicker">Auditoria de faturas laboratoriais</span>
        <h1>Compare uma fatura</h1>
        <p>Escolha sua tabela de referência, selecione a fatura e veja as diferenças abaixo.</p>
      </header>

      {erro && (
        <div className="conferencia-error">
          <XCircle size={17} />
          {erro}
        </div>
      )}

      {!resultado ? (
        <>
          <div className="cvx-aviso">
            <Lock size={15} />
            Os arquivos são lidos neste navegador e não são enviados. A referência fica salva neste navegador até você removê-la ou limpar os dados do navegador.
          </div>

          <section className="cvx-passo">
            <div className="cvx-passo-head">
              <span className="cvx-passo-num">1</span>
              <div>
                <h2>Tabela de referência</h2>
                <p>Selecione uma já salva ou carregue outra.</p>
              </div>
            </div>

            <label className="cvx-label" htmlFor="cvx-ref-select">Referência salva neste navegador</label>
            <div className="cvx-ref-row">
              <select
                id="cvx-ref-select"
                value={referencia && referenciaAtivaId ? referenciaAtivaId : ''}
                onChange={(event) => event.target.value && usarReferenciaSalva(event.target.value)}
              >
                <option value="">{referenciasSalvas.length ? 'Selecione uma referência' : 'Nenhuma referência salva'}</option>
                {referenciasSalvas.map((item) => (
                  <option key={item.id} value={item.id}>{item.nome} · {item.itens} itens</option>
                ))}
              </select>
              <label className="cvx-btn-outline">
                <Plus size={16} /> Adicionar referência
                <input
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv"
                  onChange={(event) => {
                    const arquivoEscolhido = event.target.files?.[0];
                    event.target.value = '';
                    if (arquivoEscolhido) selecionarArquivo(arquivoEscolhido, 'referencia');
                  }}
                />
              </label>
            </div>

            {referencia && (
              <div className="cvx-arquivo">
                <FileSpreadsheet size={20} />
                <div className="cvx-arquivo-info">
                  <strong>{referencia.name}</strong>
                  <small>{formatarTamanho(referencia.size)} · {linhasReferencia.length} procedimentos lidos · neste navegador{layoutAplicado.referencia ? ` · layout “${layoutAplicado.referencia}”` : ''}</small>
                </div>
                {matrizes.referencia && (
                  <button type="button" className="cvx-btn-ajustar" onClick={() => abrirAjuste('referencia')}>Ajustar campos</button>
                )}
                {referenciaAtivaId && (
                  <button type="button" className="cvx-lixeira" title="Remover referência deste navegador" onClick={() => excluirReferencia(referenciaAtivaId)}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            )}
            {avisoReferencia && <p className="cvx-aviso-erro">{avisoReferencia}</p>}
          </section>

          <section className="cvx-passo">
            <div className="cvx-passo-head">
              <span className="cvx-passo-num">2</span>
              <div>
                <h2>Fatura para comparar</h2>
                <p>PDF pesquisável ou planilha Excel.</p>
              </div>
            </div>

            <div className="cvx-dropzone">
              <FileText size={20} />
              <div className="cvx-arquivo-info">
                <strong>{fatura ? fatura.name : 'Nenhuma fatura selecionada'}</strong>
                <small>{fatura ? `${formatarTamanho(fatura.size)} · ${linhasFatura.length} itens lidos${layoutAplicado.fatura ? ` · layout “${layoutAplicado.fatura}”` : ''}` : 'PDF, XLSX, XLS ou CSV · máximo 50 MB'}</small>
              </div>
              {fatura && matrizes.fatura && (
                <button type="button" className="cvx-btn-ajustar" onClick={() => abrirAjuste('fatura')}>Ajustar campos</button>
              )}
              <label className="cvx-btn-outline">
                <Upload size={16} /> {fatura ? 'Trocar fatura' : 'Selecionar fatura'}
                <input
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv"
                  onChange={(event) => {
                    const arquivoEscolhido = event.target.files?.[0];
                    event.target.value = '';
                    if (!arquivoEscolhido) return;
                    if (arquivoEscolhido.size > 50 * 1024 * 1024) { setErro('A fatura passa de 50 MB.'); return; }
                    selecionarArquivo(arquivoEscolhido, 'fatura');
                  }}
                />
              </label>
            </div>
            <p className="cvx-dica">PDFs precisam ter texto selecionável. Se o formato da planilha variar, use “Ajustar campos” e salve um perfil para reutilizar.</p>
          </section>

          {ajuste && matrizes[ajuste.tipo] && (() => {
            const info = matrizes[ajuste.tipo];
            const cabecalhos = cabecalhosDaMatriz(info.matrix, ajuste.mapa.linhaCabecalho);
            const totalLinhas = Math.max(0, info.matrix.length - ajuste.mapa.linhaCabecalho - 1);
            const mudar = (campo, valor) => setAjuste((atual) => ({ ...atual, mapa: { ...atual.mapa, [campo]: valor } }));
            const nomeArq = ajuste.tipo === 'referencia' ? referencia?.name : fatura?.name;
            const opcoes = cabecalhos.map((c, i) => <option key={i} value={i}>{c}</option>);
            return (
              <section className="cvx-passo cvx-ajuste">
                <div className="cvx-ajuste-head">
                  <div>
                    <span className="cvx-kicker">Ajuste de leitura</span>
                    <h2>Aponte os campos {ajuste.tipo === 'referencia' ? 'da referência' : 'da fatura'}</h2>
                    <p>Arquivo: <strong>{nomeArq}</strong>. Confira a prévia e indique as colunas corretas.</p>
                  </div>
                  <button type="button" className="cvx-ajuste-fechar" onClick={() => setAjuste(null)} aria-label="Fechar"><X size={18} /></button>
                </div>

                <div className="cvx-ajuste-grid">
                  <label><span>Nome do procedimento <em>obrigatório</em></span>
                    <select value={ajuste.mapa.nome} onChange={(e) => mudar('nome', Number(e.target.value))}>{opcoes}</select>
                  </label>
                  <label><span>Valor / preço <em>obrigatório</em></span>
                    <select value={ajuste.mapa.valor} onChange={(e) => mudar('valor', Number(e.target.value))}>
                      <option value={-1}>Selecione a coluna</option>{opcoes}
                    </select>
                  </label>
                  <label><span>Código <small>opcional</small></span>
                    <select value={ajuste.mapa.codigo} onChange={(e) => mudar('codigo', Number(e.target.value))}>
                      <option value={-1}>Não usar código</option>{opcoes}
                    </select>
                  </label>
                  <label><span>Quantidade <small>opcional</small></span>
                    <select value={ajuste.mapa.quantidade} onChange={(e) => mudar('quantidade', Number(e.target.value))}>
                      <option value={-1}>Considerar 1 por linha</option>{opcoes}
                    </select>
                  </label>
                  <label><span>O preço selecionado representa</span>
                    <select value={ajuste.mapa.valorTipo} onChange={(e) => mudar('valorTipo', e.target.value)}>
                      <option value="unitario">Preço unitário</option>
                      <option value="total">Valor total da linha</option>
                    </select>
                  </label>
                </div>

                <div className="cvx-previa-head">
                  <strong>Prévia detectada</strong>
                  <small>{totalLinhas} linha(s) · {previaAjuste.length} válida(s) · {cabecalhos.length} coluna(s) · aba {info.aba}</small>
                </div>
                <div className="cvx-previa">
                  <table>
                    <thead>
                      <tr>
                        <th>Procedimento</th>
                        {ajuste.mapa.codigo >= 0 && <th>Código</th>}
                        {ajuste.mapa.quantidade >= 0 && <th>Qtd.</th>}
                        <th>{ajuste.mapa.valorTipo === 'total' ? 'Valor unitário (calculado)' : 'Valor (R$)'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previaAjuste.slice(0, 50).map((linha) => (
                        <tr key={linha.id}>
                          <td>{linha.descricao}</td>
                          {ajuste.mapa.codigo >= 0 && <td>{linha.codigo}</td>}
                          {ajuste.mapa.quantidade >= 0 && <td>{linha.quantidade}</td>}
                          <td>{formatarValor(linha.valorUnitario)}</td>
                        </tr>
                      ))}
                      {!previaAjuste.length && <tr><td colSpan={4}>Nenhuma linha válida com essas colunas.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <p className="cvx-dica">O mapeamento fica só neste navegador. Revise o nome e o preço antes de confirmar; cabeçalhos e valores não reconhecidos não são adivinhados.</p>

                <div className="cvx-ajuste-rodape">
                  <label>Salvar este layout para reutilizar (opcional)
                    <input value={ajuste.nomeLayout} onChange={(e) => setAjuste((atual) => ({ ...atual, nomeLayout: e.target.value }))} placeholder="Ex.: Fatura Laboratório Anchieta" />
                  </label>
                  <button type="button" className="cvx-btn-outline" onClick={() => setAjuste(null)}>Cancelar</button>
                  <button type="button" className="cvx-btn-primario" onClick={aplicarAjuste}><Check size={16} /> Usar estes campos</button>
                </div>
              </section>
            );
          })()}

          <button type="button" className="cvx-comparar" onClick={calcular} disabled={!podeCalcular}>
            <Scale size={17} /> {carregando ? 'Lendo arquivos…' : 'Comparar fatura'}
          </button>
        </>
      ) : (
        <section className="conferencia-result-card">
          <div className="conferencia-result-head">
            <div>
              <span className="conferencia-kicker">Resultado da comparação</span>
              <h2>Diferenças encontradas</h2>
            </div>

            <div className="conferencia-result-competencia">
              <span>Competência</span>
              <strong>{extrairCompetencia(fatura)}</strong>
            </div>

            <div className="conferencia-result-actions">
              <button
                type="button"
                className="conferencia-secondary"
                onClick={() => exportarExcelResultado(resultado, referencia, fatura)}
              >
                <Download size={15} />
                Exportar Excel
              </button>

              <button
                type="button"
                className="conferencia-secondary"
                onClick={() => exportarPdfResultado(resultado, referencia, fatura)}
              >
                <FileDown size={15} />
                Exportar PDF
              </button>

              <button
                type="button"
                className="conferencia-secondary"
                onClick={reiniciar}
              >
                <RotateCcw size={15} />
                Nova conferência
              </button>
            </div>
          </div>

          <div className="conferencia-result-search-row">
            <label className="conferencia-search">
              <Search size={16} />
              <input
                type="search"
                value={buscaProcedimento}
                onChange={(event) => setBuscaProcedimento(event.target.value)}
                placeholder="Buscar procedimento"
                aria-label="Buscar procedimento"
              />
            </label>
          </div>

          <div className="conferencia-report-meta">
            <span>
              Referência: <strong>{nomeArquivo(referencia)}</strong>
            </span>
            <span>
              Fatura: <strong>{nomeArquivo(fatura)}</strong>
            </span>
            <span className="conferencia-local-badge">
              Processado localmente
            </span>
          </div>

          <div className="conferencia-summary">
            <div>
              <strong>{resumo.total}</strong>
              <span>Itens comparados</span>
            </div>
            <div>
              <strong>{formatarValor(resumo.totalPago)}</strong>
              <span>Total pago</span>
            </div>
            <div>
              <strong>{formatarValor(resumo.totalReferencia)}</strong>
              <span>Total de referência</span>
            </div>
            <div className={resumo.diferencaTotal > 0 ? 'conferencia-summary-warning' : ''}>
              <strong>{formatarValor(resumo.diferencaTotal)}</strong>
              <span>Diferença para mais da referência</span>
            </div>
          </div>

          {buscaProcedimento && (
            <div className="conferencia-search-result">
              Mostrando {linhasResultado.length} de {resultado.linhas.length} procedimentos
            </div>
          )}

          <div className="conferencia-table-wrapper">
            <table className="conferencia-table">
              <thead>
                <tr>
                  <th>Procedimento</th>
                  <th>Qtd.</th>
                  <th>Pago / un.</th>
                  <th>Referência / un.</th>
                  <th>Dif. / un.</th>
                  <th>Diferença total</th>
                  <th>Diferença %</th>
                </tr>
              </thead>
              <tbody>
                {linhasResultado.map((item, indice) => (
                  <tr
                    key={`${item.chave}-${indice}`}
                    className={`conferencia-row conferencia-row-${item.tipo}`}
                  >
                    <td>
                      <strong>{item.descricao}</strong>
                      {item.codigo && <small>Cód. {item.codigo}</small>}
                    </td>
                    <td>{item.quantidade}</td>
                    <td>{formatarValor(item.pagoUnitario)}</td>
                    <td>{formatarValor(item.referenciaUnitario)}</td>
                    <td>{formatarValor(item.diferencaUnitario)}</td>
                    <td>{formatarValor(item.diferencaTotal)}</td>
                    <td>
                      {item.percentual === null || item.percentual === undefined
                        ? '—'
                        : `${item.percentual.toLocaleString('pt-BR', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1
                          })}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
}
